import React, { ChangeEvent, useState } from 'react';
import '../../assets/css/product.css';
import { Modal, Button } from 'react-bootstrap';
import { FaMinus, FaPlus, FaShoppingBag } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState } from '@/core/redux/store';
import { useNavigate } from 'react-router';
import { addCartItem } from '@/core/redux/cartSlice';
import { toast } from 'react-toastify';
import { routePrefix, routes } from '@/utils/routes/routes';
import { postData } from '@/services/api.service';
import { TbCircleCheck } from 'react-icons/tb';

interface ProductType {
  id: number;
  name: string;
  price: number;
  image: string;
  qty: number;
  category_id: number;
  meta_description?: string;
}

interface ProductViewProps {
  isOpen: boolean;
  onClose: () => void;
  product: any;
}

const ProductView: React.FC<ProductViewProps> = ({
  isOpen,
  onClose,
  product,
}) => {
  const [quantity, setQuantity] = useState<number>(1);
  const [selectedVariant, setSelectedVariant] = useState<string | null>('');
  const [expressWashSelected, setExpressWashSelected] = useState<boolean>(false);

  const user = store.getState().common.userDetails;
  const isLoggedIn = store.getState().common.isLoading;

  const currencyCode = useSelector(
    (state: RootState) => state.common.header?.currency?.symbol
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleQuantityChange = (newValue: number) => {
    if (newValue >= 1 && newValue <= product.qty) {
      setQuantity(newValue);
    }
  };

  const handleVariantClick = (variantName: string) => {
    setSelectedVariant(variantName);
  };

  const handleIncrement = () => {
    handleQuantityChange(quantity + 1);
  };

  const handleDecrement = () => {
    handleQuantityChange(quantity - 1);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value)) {
      handleQuantityChange(value);
    }
  };

  const handleExpressWashToggle = (checked: boolean) => {
    setExpressWashSelected(checked);
  };

  const addToCart = async () => {
    const itemId = expressWashSelected ? `${product?.id}_express` : `${product?.id}_regular`;
    const itemName = expressWashSelected ? `${product?.name} (Express Wash)` : product?.name;

    const basePrice = parseFloat(product?.price?.toString()) || 0;
    const expressAmount = parseFloat(product?.express_wash_amount?.toString()) || 0;
    const itemPrice = expressWashSelected ? expressAmount : basePrice;

    const AddToCartProduct = {
      user_id: user?.id,
      id: itemId,
      name: itemName,
      price: itemPrice,
      image: `${product.image_path}${product.image}`,
      category_id: product?.category_id,
      quantity: quantity,
      variant: expressWashSelected ? 'express' : 'regular',
      service_mode: product?.service_mode,
      process_time: product?.process_time || 0,
      type: product?.type || '',
      express_wash_selected: expressWashSelected,
      express_wash_amount: expressWashSelected ? expressAmount : 0,
      combo_details: product?.combo_details || [],
    };
    dispatch(addCartItem(AddToCartProduct));
    const messageType = expressWashSelected ? 'Express wash product' : 'Product';
    toast.success(`${messageType} added to cart successfully!`);
    onClose();
  };

  const goToProductDetail = (id: any) => {
    navigate(`${routePrefix}/product-details/${id}`);
  };

  return (
    <Modal show={isOpen} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{product?.name}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="row h-100">
          <div className="col-lg-4 col-md-5 col-12 mb-3 mb-md-0">
            <div className="modal-image">
              <img
                src={`${product.image_path}${product.image}`}
                alt={product.name}
              />
            </div>
          </div>

          <div className="col-lg-8 col-md-7 col-12">
            <div className="prod-view-modal-details h-100 d-flex flex-column">
              <div className="flex-grow-1">
                <h2>{product.name}</h2>
                <h4 className="price">
                  {currencyCode}&nbsp;{product.price}
                </h4>
                {product?.qty > 0 && (
                  <div className="mb-2">
                    <span className="stock-tag">
                      <TbCircleCheck /> In Stock
                    </span>
                  </div>
                )}
                <div
                  className="item-description mb-md-3 mb-0"
                  dangerouslySetInnerHTML={{ __html: product?.product_details?.replace(/&lt;/g, '<').replace(/&gt;/g, '>') || product?.meta_description?.replace(/&lt;/g, '<').replace(/&gt;/g, '>') || 'No description available.' }}
                />

                {/* Combo Details Section */}
                {product?.combo_details && product.combo_details.length > 0 && (
                  <div className="combo-details-section mb-3 p-3 border rounded bg-light">
                    <h5 className="mb-3 text-primary">
                      <i className="fas fa-box-open me-2"></i>
                      Combo Product Details
                    </h5>

                    <div className="combo-products-list">
                      {product.combo_details.map((combo: any, index: number) => (
                        <div key={index} className="combo-item d-flex align-items-center mb-2 p-2 bg-white rounded">
                          <img
                            src={combo.combo_product?.image_path}
                            alt={combo.combo_product?.name}
                            style={{
                              width: '50px',
                              height: '50px',
                              objectFit: 'cover',
                              borderRadius: '4px',
                              marginRight: '12px'
                            }}
                          />
                          <div className="flex-grow-1">
                            <h6 className="mb-0 small">{combo.combo_product_name}</h6>
                            <p className="mb-0 text-muted small">
                              Code: {combo.combo_product?.code} | Qty: {combo.quantity}
                            </p>
                          </div>
                          <div className="text-end">
                            <p className="mb-0 small font-weight-bold">
                              {currencyCode}&nbsp;{parseFloat(combo.price).toFixed(2)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="combo-total mt-3 pt-2 border-top">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="font-weight-bold">Total Combo Value:</span>
                        <span className="text-primary font-weight-bold">
                          {currencyCode}&nbsp;
                          {product.combo_details
                            .reduce((total: number, combo: any) => total + combo.subtotal, 0)
                            .toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                {/* Express Wash Option */}
                {product?.express_wash_amount && product.express_wash_amount > 0 && (
                  <div className="express-wash-option mt-3 mb-3">
                    <div className="d-flex align-items-center justify-content-between p-3 border rounded bg-light">
                      <div className="d-flex align-items-center">
                        <span className="express-wash-label me-3">
                          <strong>Express Wash:</strong> {currencyCode}{product.express_wash_amount}
                        </span>
                        <small className="text-muted">(Fast track service)</small>
                      </div>
                      <label className="toggle-switch mb-0">
                        <input
                          type="checkbox"
                          checked={expressWashSelected}
                          onChange={(e) => handleExpressWashToggle(e.target.checked)}
                        />
                        <span className="toggle-slider"></span>
                      </label>
                    </div>
                  </div>
                )}

                <div className="col-md-10 form-group mt-md-2 mt-0">
                  <ul className="d-flex list-unstyled flex-wrap gap-2">
                    {product?.variant_values?.map((variant: any, index: number) => {
                      const name = variant?.variant?.name;
                      const isActive = selectedVariant === name;
                      return (
                        <li
                          key={index}
                          className={`variant_val px-3 py-1 border rounded ${isActive
                            ? 'bg-primary text-white'
                            : 'bg-light text-dark'
                            }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleVariantClick(name)}
                        >
                          {name}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>
              {/* Show Add to Cart for all products except out of stock */}
              {(
                <form
                  className="d-flex"
                  style={{ gap: '10px' }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    addToCart();
                  }}
                >
                  <div className="d-flex align-items-center border rounded px-2 py-1">
                    <Button
                      variant="light"
                      onClick={handleDecrement}
                      disabled={quantity <= 1}
                    >
                      <FaMinus />
                    </Button>
                    <input
                      type="number"
                      className="form-control mx-2"
                      style={{ width: '60px' }}
                      value={quantity}
                      min={1}
                      max={product.stock || product.qty || 10}
                      onChange={handleInputChange}
                    />
                    <Button
                      variant="light"
                      onClick={handleIncrement}
                      disabled={quantity >= (product.stock || product.qty || 10)}
                    >
                      <FaPlus />
                    </Button>
                  </div>
                  <Button type="submit" variant="success">
                    <FaShoppingBag className="me-2" />
                    Add to cart
                  </Button>
                </form>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default ProductView;
